import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useContentAdmin } from '../../hooks/useEditableContent';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Switch } from '../ui/switch';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash2,
  History,
  RotateCcw,
  Save,
  Loader2,
  FileText,
  Eye,
  EyeOff,
  Sparkles
} from 'lucide-react';

const ContentManager = () => {
  const { token } = useAuth();
  const {
    contents,
    loading,
    error,
    fetchAll,
    create,
    update,
    remove,
    getVersions,
    restoreVersion,
    seedDefaults
  } = useContentAdmin(token);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [editingContent, setEditingContent] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showVersionsDialog, setShowVersionsDialog] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsContentId, setVersionsContentId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    key: '',
    title: '',
    content: '',
    content_type: 'text',
    category: '',
    order: 0,
    is_published: true
  });

  useEffect(() => {
    if (token) {
      fetchAll();
    }
  }, [token, fetchAll]);

  // Get unique categories from contents
  const categories = ['all', ...new Set(contents.map(c => c.category).filter(Boolean))];

  // Filter contents by category
  const filteredContents = selectedCategory === 'all'
    ? contents
    : contents.filter(c => c.category === selectedCategory);

  const handleCreate = async () => {
    if (!formData.key || !formData.title || !formData.content) {
      toast.error('Key, title, and content are required');
      return;
    }

    setSaving(true);
    try {
      await create(formData);
      setShowCreateDialog(false);
      setFormData({
        key: '',
        title: '',
        content: '',
        content_type: 'text',
        category: '',
        order: 0,
        is_published: true
      });
      toast.success('Content created');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!editingContent) return;

    setSaving(true);
    try {
      await update(editingContent.id, formData);
      setEditingContent(null);
      toast.success('Content updated');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      await remove(id);
      toast.success('Content deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleViewVersions = async (contentId) => {
    try {
      const versionList = await getVersions(contentId);
      setVersions(versionList);
      setVersionsContentId(contentId);
      setShowVersionsDialog(true);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleRestore = async (versionId) => {
    if (!window.confirm('Restore this version? Current content will be overwritten.')) return;

    try {
      await restoreVersion(versionsContentId, versionId);
      setShowVersionsDialog(false);
      toast.success('Version restored');
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const result = await seedDefaults();
      toast.success(`Created ${result.created} content blocks, ${result.skipped} already existed`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const openEditDialog = (content) => {
    setFormData({
      key: content.key,
      title: content.title,
      content: content.content,
      content_type: content.content_type,
      category: content.category || '',
      order: content.order,
      is_published: content.is_published
    });
    setEditingContent(content);
  };

  const openCreateDialog = () => {
    setFormData({
      key: '',
      title: '',
      content: '',
      content_type: 'text',
      category: selectedCategory === 'all' ? '' : selectedCategory,
      order: 0,
      is_published: true
    });
    setShowCreateDialog(true);
  };

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600">{error}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="content-manager">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Content Manager
              </CardTitle>
              <CardDescription>
                Manage editable content blocks displayed throughout the site
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleSeedDefaults}>
                <Sparkles className="w-4 h-4 mr-2" />
                Seed Defaults
              </Button>
              <Button onClick={openCreateDialog}>
                <Plus className="w-4 h-4 mr-2" />
                Add Content
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Category Filter */}
          <div className="flex items-center gap-4 mb-6">
            <Label>Filter by Category:</Label>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>
                    {cat === 'all' ? 'All Categories' : cat}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Content Table */}
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredContents.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No content found. Click "Add Content" or "Seed Defaults" to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Key</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContents.map(content => (
                  <TableRow key={content.id}>
                    <TableCell className="font-mono text-sm">{content.key}</TableCell>
                    <TableCell className="font-medium">{content.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{content.category || 'none'}</Badge>
                    </TableCell>
                    <TableCell>{content.content_type}</TableCell>
                    <TableCell>
                      {content.is_published ? (
                        <Badge className="bg-green-100 text-green-700">
                          <Eye className="w-3 h-3 mr-1" />
                          Published
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <EyeOff className="w-3 h-3 mr-1" />
                          Draft
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(content.updated_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(content)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewVersions(content.id)}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(content.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog 
        open={showCreateDialog || !!editingContent} 
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setEditingContent(null);
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingContent ? 'Edit Content' : 'Create Content'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Key (unique identifier)</Label>
                <Input
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  placeholder="e.g., about.mission"
                  disabled={!!editingContent}
                />
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Human-readable title"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="e.g., about, homepage"
                />
              </div>
              <div className="space-y-2">
                <Label>Content Type</Label>
                <Select 
                  value={formData.content_type} 
                  onValueChange={(v) => setFormData({ ...formData, content_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="text">Text</SelectItem>
                    <SelectItem value="html">HTML</SelectItem>
                    <SelectItem value="markdown">Markdown</SelectItem>
                    <SelectItem value="json">JSON</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Order</Label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Content</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={8}
                placeholder="Enter content..."
              />
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.is_published}
                onCheckedChange={(v) => setFormData({ ...formData, is_published: v })}
              />
              <Label>Published (visible to public)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowCreateDialog(false);
                setEditingContent(null);
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={editingContent ? handleUpdate : handleCreate}
              disabled={saving}
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingContent ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Versions Dialog */}
      <Dialog open={showVersionsDialog} onOpenChange={setShowVersionsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
          </DialogHeader>

          <div className="max-h-96 overflow-y-auto">
            {versions.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No versions found</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Updated By</TableHead>
                    <TableHead>Note</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {versions.map((version, index) => (
                    <TableRow key={version.id}>
                      <TableCell>
                        {new Date(version.updated_at).toLocaleString()}
                      </TableCell>
                      <TableCell>{version.updated_by}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {version.change_note || '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {index > 0 && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRestore(version.id)}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                        )}
                        {index === 0 && (
                          <Badge variant="outline">Current</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
